import { useCallback, useMemo, useRef, useState } from "react";
import Link from "next/link";
import styled from "styled-components";

import {
  BackLink,
  ColorAA,
  Fields,
  FieldType,
  FilterBar,
  FixedToolbarItem,
  FlexToolbarItem,
  IconButton,
  Page,
  Toolbar,
  TooltipProvider,
} from "@bagel-web/components";
import EventStreamHeaderDetail from "./EventStreamHeaderDetail";
import MomentDetailSidebar from "./sidebar/MomentDetailSidebar";
import { formatTimestamp } from "../util";
import {
  CommonEventStream,
  CommonFactEventMessage,
  CommonKVMessage,
  CommonMomentDetail,
  CommonMomentEvent,
  CommonMomentSummary,
  CommonSessionDetails,
} from "../types/Session";
import { BagelForkEventStreamCallback, EventStreamSettings } from "../types";
import MomentNavigator from "./MomentNavigator";
import { Spinner } from "react-bootstrap";
import EventStreamSettingsButton from "./EventStreamSettingsButton";
import { useLocalStorage } from "../util/localStorage";
import useFilters from "../filter/useFilters";
import MessageTypeSelect from "./MessageTypeSelect";
import { EventRow } from "./message/Message";

const MomentLink = styled.a`
  font-weight: bold;
  width: fit-content;
  cursor: pointer;
`;

const MomentTimestamp = styled.span`
  font-style: italic;
  color: ${ColorAA.grey};
  margin: 0px 10px;
`;

function MomentHeader({ moment }: { moment: CommonMomentSummary }) {
  return (
    <>
      <MomentLink href={`#moment-${moment.id}`}>Moment {moment.id}</MomentLink>
      <MomentTimestamp>{formatTimestamp(moment.created)}</MomentTimestamp>
    </>
  );
}

function getPreviousFact(
  event: CommonMomentEvent<CommonFactEventMessage | CommonKVMessage>,
  prevMoment?: CommonMomentSummary
) {
  let prevEvent;
  if (event.message.message_type === "FactEventMessage") {
    prevEvent = prevMoment?.events?.find(
      (e) =>
        e.message.message_type === "FactEventMessage" &&
        event.message.message_type === "FactEventMessage" &&
        e.message.fact_name === event.message.fact_name
    );
  } else if (event.message.message_type === "KVMessage") {
    prevEvent = prevMoment?.events?.find(
      (e) =>
        e.message.message_type === "KVMessage" &&
        event.message.message_type === "KVMessage" &&
        e.message.key === event.message.key
    );
  }
  return prevEvent;
}

const ShowAllOverrideLink = styled.a`
  margin-left: 4px;
`;

const MomentHeaderCell = styled.td`
  padding-top: 10px;
`;

function DisplayMoment({
  moment,
  prevMoment,
  highlightedFnCallId,
  onHighlightFnCallId,
  onClick,
  displaySettings,
  showMessageTypes,
}: {
  moment: CommonMomentSummary;
  prevMoment?: CommonMomentSummary;
  highlightedFnCallId: string | null;
  onHighlightFnCallId: (fnCallId: string | null) => void;
  onClick: (momentId: number) => void;
  displaySettings: EventStreamSettings;
  showMessageTypes: Array<string>;
}) {
  const [showAllOverride, setShowAllOverride] = useState(false);

  const prevFactMap = useMemo(() => {
    const prevFacts: Record<string, CommonMomentEvent> = {};
    moment.events.forEach((event) => {
      if (event.message.message_type in ["KVMessage", "FactEventMessage"]) {
        const prevFact = getPreviousFact(
          event as CommonMomentEvent<CommonKVMessage | CommonFactEventMessage>,
          prevMoment
        );
        if (prevFact) {
          prevFacts[event.id] = prevFact;
        }
      }
    });
    return prevFacts;
  }, [moment, prevMoment]);

  const { displayedEvents, hiddenMessageCount } = useMemo<{
    displayedEvents: Array<CommonMomentEvent>;
    hiddenMessageCount: number;
  }>(() => {
    if (showAllOverride) {
      return {
        displayedEvents: moment.events,
        hiddenMessageCount: 0,
      };
    }
    const filteredEvents =
      showMessageTypes.length > 0
        ? moment.events.filter((event) => {
            return showMessageTypes.includes(event.message.message_type);
          })
        : moment.events;

    const displayedEvents = displaySettings.hideUnchangedFacts
      ? filteredEvents.filter((event) => {
          const prevMessage = prevFactMap[event.id];

          // Exclude unchanged facts unless they have been revealed
          if (event.message.message_type === "FactEventMessage") {
            return (
              prevMessage?.message.message_type !== "FactEventMessage" ||
              prevMessage?.message.content !== event.message.content
            );
          } else if (event.message.message_type === "KVMessage") {
            return (
              prevMessage?.message.message_type !== "KVMessage" ||
              prevMessage?.message.value !== event.message.value
            );
          }
          return true;
        })
      : filteredEvents;

    const hiddenMessageCount = moment.events.length - displayedEvents.length;
    return { displayedEvents, hiddenMessageCount };
  }, [
    moment,
    prevMoment,
    displaySettings.hideUnchangedFacts,
    showAllOverride,
    prevFactMap,
    JSON.stringify(showMessageTypes),
  ]);

  // TODO: Make event message type clear and consistent (game_client labeling is confusing, separate type from source more clearly)

  return (
    <>
      <tr>
        <MomentHeaderCell
          colSpan={5}
          id={`moment-${moment.id}`}
          data-moment-id={moment.id}
          className="moment-summary"
        >
          <MomentHeader moment={moment} />
          <TooltipProvider tooltip="Moment inspector">
            <IconButton
              className="bi-eye-fill"
              onClick={() => onClick(moment.id)}
            />
          </TooltipProvider>
          <div style={{ float: "right" }}>
            {(hiddenMessageCount > 0 || showAllOverride) && (
              <ShowAllOverrideLink
                href="#"
                onClick={() => setShowAllOverride(!showAllOverride)}
              >
                {showAllOverride
                  ? "Hide filtered messages"
                  : `Show ${hiddenMessageCount} additional ${hiddenMessageCount == 1 ? "message" : "messages"}`}
                <i
                  style={{ marginLeft: "5px" }}
                  className={
                    showAllOverride ? "bi-chevron-up" : "bi-chevron-down"
                  }
                />
              </ShowAllOverrideLink>
            )}
          </div>
        </MomentHeaderCell>
      </tr>
      {displayedEvents.map((event) => {
        return (
          <EventRow
            key={event.id}
            event={event}
            prevEvent={prevFactMap[event.id]}
            highlightedFnCallId={highlightedFnCallId}
            onHighlightFnCallId={onHighlightFnCallId}
            displaySettings={displaySettings}
          />
        );
      })}
    </>
  );
}

const EVENT_STREAM_SETTINGS_LOCAL_STORAGE_KEY = "event-stream-settings";

const filterFields: Fields = [
  {
    name: "messageTypes",
    label: "Message types",
    type: FieldType.MessageType,
    Component: MessageTypeSelect,
    colSpan: 2,
  },
];

function EventStream({
  sessionId,
  agentId,
  eventStream,
  refetch,
  isPending,
  isRefetching,
  fetchSession,
  fetchEventStreamDetail,
  forkEventStream,
  reverse,
  onSetReverse,
}: {
  sessionId: string;
  agentId: string;
  eventStream?: CommonEventStream;
  refetch: () => void;
  isPending: boolean;
  isRefetching: boolean;
  fetchSession: (sessionId: string) => Promise<CommonSessionDetails>;
  fetchEventStreamDetail: (
    sessionId: string,
    agentId: string,
    momentId: number
  ) => Promise<CommonMomentDetail>;
  forkEventStream: BagelForkEventStreamCallback;
  reverse: boolean;
  onSetReverse: (reverse: boolean) => void;
}) {
  const startOfStream = useRef<null | HTMLDivElement>(null);
  const endOfStream = useRef<null | HTMLDivElement>(null);

  const [detailMomentId, setDetailMomentId] = useState<number | null>(null);

  const handleClickMoment = useCallback(
    (momentId: number) => {
      setDetailMomentId(momentId);
    },
    [setDetailMomentId]
  );

  const { filterCriteria, handleSetFilters, handleClearFilter } = useFilters(
    filterFields,
    "event-stream"
  );

  const showMessageTypes = filterCriteria["messageTypes"]
    ? JSON.parse(filterCriteria["messageTypes"].value).map(
        ({ value }: { value: string }) => value
      )
    : [];

  const [displaySettings, setDisplaySettings] = useState<EventStreamSettings>(
    useLocalStorage<EventStreamSettings>().getItem(
      EVENT_STREAM_SETTINGS_LOCAL_STORAGE_KEY
    ) || {
      showFactDiffs: true,
      hideUnchagedFactLines: true,
      hideUnchangedFacts: true,
      showMessageWhitespace: true,
    }
  );
  const [highlightedFnCallId, setHighlightedFnCallId] = useState<string | null>(
    null
  );

  return (
    <Page>
      <Page.Header className="shadow">
        <BackLink href={`/sessions/${sessionId}`} LinkComponent={Link}>
          Session
        </BackLink>
        <Toolbar>
          <h2>Message stream</h2>
          <FlexToolbarItem>
            <EventStreamHeaderDetail
              sessionId={sessionId}
              agentId={agentId}
              fetchSession={fetchSession}
            />
          </FlexToolbarItem>
          <FixedToolbarItem>
            <MomentNavigator
              startOfStream={startOfStream.current}
              endOfStream={endOfStream.current}
              moments={eventStream?.moments || []}
              detailMomentId={detailMomentId}
              refetch={refetch}
              isRefetching={isRefetching}
              reverse={reverse}
              onSetReverse={onSetReverse}
            />
          </FixedToolbarItem>
          <FixedToolbarItem>
            <EventStreamSettingsButton
              settings={displaySettings}
              onSettingsChanged={(settings) => {
                useLocalStorage<EventStreamSettings>().setItem(
                  EVENT_STREAM_SETTINGS_LOCAL_STORAGE_KEY,
                  settings
                );
                setDisplaySettings(settings);
              }}
            />
          </FixedToolbarItem>
          <FixedToolbarItem>
            <FilterBar
              fields={filterFields}
              criteria={filterCriteria}
              onSetFilters={handleSetFilters}
              onClear={handleClearFilter}
              showChips={false}
            />
          </FixedToolbarItem>
        </Toolbar>
      </Page.Header>
      <Page.Body>
        <MomentDetailSidebar
          sessionId={sessionId}
          agentId={agentId}
          momentId={detailMomentId}
          fetchEventStreamDetail={fetchEventStreamDetail}
          fetchSession={fetchSession}
          forkEventStream={forkEventStream}
          onHide={() => setDetailMomentId(null)}
        />
        <div ref={startOfStream} />

        {isPending && <Spinner />}
        {eventStream &&
          (!eventStream?.moments || eventStream.moments.length === 0) && (
            <div>No moments found for session</div>
          )}
        <table style={{ width: "100%" }}>
          <tbody>
            {eventStream?.moments.map((moment, index, moments) => {
              return (
                <DisplayMoment
                  key={moment.id}
                  moment={moment}
                  prevMoment={moments[index - 1]}
                  highlightedFnCallId={highlightedFnCallId}
                  onHighlightFnCallId={setHighlightedFnCallId}
                  onClick={handleClickMoment}
                  displaySettings={displaySettings}
                  showMessageTypes={showMessageTypes}
                />
              );
            })}
          </tbody>
        </table>

        <div ref={endOfStream} />
      </Page.Body>
    </Page>
  );
}

export default EventStream;
